/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { FloatNumberInput, MoneyInput } from '@/components';
import { FieldBase, useAppForm } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useSettings } from '@/domain/dienstplan/features/SettingsMindestbelegung/hooks/useSettings';
import { useTagebuchEinstellungen } from '@/domain/dienstplan/shared/hooks/useTagebuchEinstellungen/useTagebuchEinstellungen';
import { Arbeitsbereich } from '@/domain/dienstplan/shared/types/Arbeitsbereich';
import { AbsatzVorgabe } from '@/domain/dienstplan/shared/types/tagebuchEinstellungen';
import { Weekday } from '@/domain/dienstplan/shared/types/Weekday';
import { getLocaleWeekdayString } from '@/domain/dienstplan/shared/utils/getLocaleWeekdayString';
import { mapWochentagForAPI } from '@/domain/dienstplan/shared/utils/mapWochentagForAPI';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { Dienstplan_AbsatzVorgabe } from '@/gql/graphql';
import { useProfile } from '@/hooks';
import { toast } from '@/hooks/use-toast';
import { faGear } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

type SettingsMindestbelegungProps = {
  wochentag: Weekday;
  arbeitsbereich: Arbeitsbereich;
};

export function SettingsMindestbelegung({
  wochentag,
  arbeitsbereich,
}: SettingsMindestbelegungProps) {
  const { t } = useTranslation();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const [updateSettings] = useSettings();
  const { manager } = useProfile();
  const [showDialog, setShowDialog] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { umsatzplanungAnzeige, tagebuchEinstellungen } =
    useTagebuchEinstellungen(standortId);
  const einstellungen = arbeitsbereich.mindestbelegungen.find(
    (w) => w.tag === wochentag,
  )?.einstellungen;

  const formSchema = z
    .object({
      maxMitarbeiteranzahl: z.string().regex(/^\d+$/, {
        message: t(
          'domain.dienstplan.mindestbelegung.einstellungen.validationErrors',
        ),
      }),
      unbesetzteSchichten: z.enum(['yes', 'no']),
      bemerkung: z.string(),
      umsatzproduktivitaet: z.string(),
      absatzproduktivitaet: z.string(),
    })
    .superRefine((data, ctx) => {
      const value = parseInt(data.maxMitarbeiteranzahl);
      if (value <= 0) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.dienstplan.mindestbelegung.einstellungen.validationErrors',
          ),
          path: ['maxMitarbeiteranzahl'],
        });
      }
    });

  const form = useAppForm({
    defaultValues: {
      maxMitarbeiteranzahl: einstellungen?.maxAnzahlMitarbeiter.toString(),
      unbesetzteSchichten: einstellungen?.unbesetzteSchichten ? 'yes' : 'no',
      bemerkung: einstellungen?.bemerkung,
      umsatzproduktivitaet: einstellungen?.zielumsatzproduktivitaet.toString(),
      absatzproduktivitaet:
        tagebuchEinstellungen?.absatzProduktivitaet.absatzVorgabe ===
        AbsatzVorgabe.Stueck
          ? einstellungen?.zielabsatzproduktivitaet.stueckzahl.toString()
          : einstellungen?.zielabsatzproduktivitaet.mpt.toString(),
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const managerId = manager?.id;
      if (!managerId) {
        toast({
          title: 'Mindestbelegung',
          description: 'Profil konnte nicht geladen werden',
        });
        return;
      }
      const result = await updateSettings({
        input: {
          bemerkung: value.bemerkung ?? '',
          idArbeitsbereich: arbeitsbereich.id,
          managerId: managerId,
          maxMitarbeiterAnzahl: value.maxMitarbeiteranzahl
            ? parseInt(value.maxMitarbeiteranzahl)
            : 0,
          tag: mapWochentagForAPI(wochentag),
          unbesetzteSchichtenErlauben: value.unbesetzteSchichten === 'yes',
          zielabsatzproduktivitaetAbsatzvorgabe:
            tagebuchEinstellungen?.absatzProduktivitaet.absatzVorgabe ===
            AbsatzVorgabe.Stueck
              ? Dienstplan_AbsatzVorgabe.Stueck
              : Dienstplan_AbsatzVorgabe.Mpt,
          zielabsatzproduktivitaetWert: value.absatzproduktivitaet
            ? parseInt(value.absatzproduktivitaet)
            : 0,
          zielumsatzproduktivitaet: value.umsatzproduktivitaet
            ? parseInt(value.umsatzproduktivitaet)
            : 0,
        },
      });

      if (
        result.data?.dienstplan_update_einstellungen?.__typename ===
        'dienstplan_einstellungen'
      ) {
        toast({
          title: 'Mindestbelegung',
          description: 'Einstellungen erfolgreich gespeichert',
        });
        setIsOpen(false);
      }
      formApi.reset();
    },
  });

  return (
    <>
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (open === false && form.state.isDirty) {
            setShowDialog(true);
            return;
          }
          setIsOpen(open);
        }}>
        <SheetTrigger asChild>
          <Button variant="icon">
            <FontAwesomeIcon icon={faGear} />
          </Button>
        </SheetTrigger>
        <SheetContent className="flex flex-col gap-6">
          <SheetHeader className="flex flex-col space-y-1">
            <h1 className="text-neutrals-700 text-sm">
              {getLocaleWeekdayString(wochentag)}
            </h1>
            <DialogTitle className="text-neutrals-900 text-xl font-bold">
              {arbeitsbereich.name}
            </DialogTitle>
            <SheetDescription className="hidden">
              {arbeitsbereich.name}
            </SheetDescription>
          </SheetHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-4">
            <div className="flex flex-col gap-6">
              {umsatzplanungAnzeige !== 'ABSATZ_PRODUKTIVITAET' && (
                <form.AppField name="umsatzproduktivitaet">
                  {(field) => {
                    return (
                      <FieldBase
                        label={t(
                          'domain.dienstplan.mindestbelegung.einstellungen.umsatz',
                        )}>
                        <MoneyInput
                          value={field.state.value}
                          isIconVisible={false}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          unit={t(
                            'domain.controlling.umsatzplanung.ziele.€/Std.',
                          )}
                        />
                      </FieldBase>
                    );
                  }}
                </form.AppField>
              )}
              {umsatzplanungAnzeige !== 'UMSATZ_PRODUKTIVITAET' && (
                <form.AppField name="absatzproduktivitaet">
                  {(field) => {
                    return (
                      <FieldBase
                        label={t(
                          'domain.dienstplan.mindestbelegung.einstellungen.absatz',
                        )}>
                        <FloatNumberInput
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          unit={
                            tagebuchEinstellungen?.absatzProduktivitaet
                              .absatzVorgabe === AbsatzVorgabe.Stueck
                              ? t(
                                  'domain.controlling.umsatzplanung.units.Stk./Std.',
                                )
                              : t('domain.controlling.umsatzplanung.units.mpt')
                          }
                        />
                      </FieldBase>
                    );
                  }}
                </form.AppField>
              )}
              <form.AppField name="maxMitarbeiteranzahl">
                {(field) => (
                  <field.TextInput
                    label={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.mitarbeiterMax',
                    )}
                  />
                )}
              </form.AppField>
              <form.AppField name="unbesetzteSchichten">
                {(field) => (
                  <FieldBase
                    label={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.schichten',
                    )}>
                    <RadioGroup
                      className="flex gap-4"
                      onValueChange={field.handleChange}
                      value={field.state.value}>
                      <div className="flex items-center gap-2 space-y-0">
                        <RadioGroupItem id="yes" value="yes" />
                        <Label htmlFor="yes">{t('actions.yes')}</Label>
                      </div>
                      <div className="flex items-center gap-2 space-y-0">
                        <RadioGroupItem id="no" value="no" />
                        <Label htmlFor="no">{t('actions.no')}</Label>
                      </div>
                    </RadioGroup>
                  </FieldBase>
                )}
              </form.AppField>
              <form.AppField name="bemerkung">
                {(field) => (
                  <field.TextArea
                    label={t(
                      'domain.dienstplan.mindestbelegung.einstellungen.bemerkung',
                    )}
                    maxLength={250}
                  />
                )}
              </form.AppField>
            </div>
            <div className="flex w-full justify-end">
              <form.Subscribe
                selector={(state) => ({
                  isSubmitting: state.isSubmitting,
                  canSubmit: state.canSubmit,
                })}>
                {({ isSubmitting, canSubmit }) => (
                  <Button disabled={!canSubmit || isSubmitting} type="submit">
                    {t(
                      'domain.dienstplan.mindestbelegung.einstellungen.speichern',
                    )}
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </form>
        </SheetContent>
      </Sheet>
      {showDialog && (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogPortal>
            <DialogContent className="z-55">
              <DialogTitle>
                {t(
                  'domain.dienstplan.mindestbelegung.einstellungen.dialog.title',
                )}
              </DialogTitle>
              <DialogDescription>
                <div className="flex flex-col gap-8 w-full">
                  <p>
                    {t(
                      'domain.dienstplan.mindestbelegung.einstellungen.dialog.despription',
                    )}
                  </p>
                  <div className="flex justify-end w-full gap-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        form.reset();
                        setShowDialog(false);
                        setIsOpen(false);
                      }}>
                      {t(
                        'domain.dienstplan.mindestbelegung.einstellungen.dialog.verlassen',
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowDialog(false);
                      }}>
                      {t(
                        'domain.dienstplan.mindestbelegung.einstellungen.dialog.bearbeiten',
                      )}
                    </Button>
                  </div>
                </div>
              </DialogDescription>
            </DialogContent>
          </DialogPortal>
        </Dialog>
      )}
    </>
  );
}
